import { memo } from "react";
import { Box, Modal } from "@mui/material";

import Close from "@zenith/assets/icons/Close";
import { ButtonComponent } from "@zenith/components";
import type { BusinessType } from "@zenith/features/AuthZ/permissions";
import { getRoleBullets, ROLE_DEFINITIONS, TeamMemberRole } from "../../data/roles";

export interface SelectRoleModalProps {
  showModal: boolean;
  selectedRole: TeamMemberRole | null;
  businessType: BusinessType;
  onSelectRole: (role: TeamMemberRole) => void;
  onViewBreakdown: (role: TeamMemberRole) => void;
  onClose: () => void;
  onContinue: () => void;
}

const SelectRoleModal = memo(function SelectRoleModal(
  props: SelectRoleModalProps
) {
  const {
    showModal,
    selectedRole,
    businessType,
    onSelectRole,
    onViewBreakdown,
    onClose,
    onContinue,
  } = props;

  return (
    <Modal open={showModal} onClose={onClose} className="overflow-visible">
      <Box className="absolute left-1/2 top-1/2 z-0 flex max-h-[819px] w-[95%] max-w-[668px] -translate-x-1/2 -translate-y-1/2 transform flex-col overflow-y-auto rounded-[20px] bg-white px-8 py-6 outline-none">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="mr-1 flex h-4 w-4 cursor-pointer items-center justify-center border-0 bg-transparent p-0"
          >
            <Close />
          </button>
        </div>
        <h2 className="mt-5 text-2xl font-semibold leading-8 text-neutral-900">
          Select Role
        </h2>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {ROLE_DEFINITIONS.map((role) => {
            const isSelected = selectedRole === role.id;
            return (
              <div
                key={role.id}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={() => onSelectRole(role.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectRole(role.id);
                  }
                }}
                className={`flex min-h-[278px] cursor-pointer flex-col rounded-xl border bg-white px-[25px] pb-4 pt-6 transition-colors ${
                  isSelected
                    ? "border-primary-250"
                    : "border-neutral-200 hover:border-neutral-300"
                }`}
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-base font-medium text-neutral-800">
                    {role.label}
                  </span>
                  {isSelected && (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                      className="-mr-[9px] -mt-2 shrink-0"
                    >
                      <rect width="20" height="20" rx="10" fill="#DA291C" />
                      <path
                        d="M13.333 10C13.333 6.318 10.348 3.333 6.666 3.333C2.984 3.333 0 6.318 0 10C0 13.682 2.984 16.667 6.666 16.667C10.348 16.667 13.333 13.682 13.333 10Z"
                        transform="translate(3.333 0)"
                        fill="white"
                        fillOpacity="0.4"
                      />
                      <path
                        d="M13.333 10C13.333 6.318 10.348 3.333 6.666 3.333C2.984 3.333 0 6.318 0 10C0 13.682 2.984 16.667 6.666 16.667C10.348 16.667 13.333 13.682 13.333 10Z"
                        transform="translate(3.333 0)"
                        stroke="white"
                      />
                      <path
                        d="M3.999 10.333L5.666 12L9.332 8"
                        transform="translate(3.333 0)"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <ul className="mb-4 flex-1 list-disc pl-5 text-[13px] leading-[22px] text-neutral-600">
                  {getRoleBullets(role.id, businessType).map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewBreakdown(role.id);
                  }}
                  className="self-start text-[13px] font-normal leading-[18px] text-primary-300 underline"
                >
                  View permission breakdown&gt;
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4">
          <ButtonComponent
            id="select-role-cancel"
            type="secondary"
            size="large"
            label="Cancel"
            className="w-full !rounded-lg"
            onClick={onClose}
          />
          <ButtonComponent
            id="select-role-continue"
            type="primary"
            size="large"
            label="Continue"
            className="w-full !rounded-lg disabled:border disabled:border-neutral-100 disabled:text-neutral-100"
            disabled={!selectedRole}
            onClick={onContinue}
          />
        </div>
      </Box>
    </Modal>
  );
});

export default SelectRoleModal;
